import { Component, EventEmitter, forwardRef, inject, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance } from '@workflow/class/field.service';
import { RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { GlobalService } from '@workflow/ecp-shared/service/global/global.service';
import { IEconsentAcknowledgment, ILegalConsent } from '@workflow/ecp-workflow/dynamic-app/types/legal-consent.type';
import { CommonService } from '@workflow/legacy';
import { firstValueFrom } from 'rxjs';

export const CONSENT_CONTROL_VALUE_ACCESSOR = {
  multi: true,
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ConsentComponent)
};

/**
 * A class representing consent checkbox
 */
@Component({
  providers: [CONSENT_CONTROL_VALUE_ACCESSOR],
  selector: 'dx-consent',
  templateUrl: './consent.component.html'
})
export class ConsentComponent {
  private innerValue?: boolean;
  /** Local variable to hold Consent Library */
  public checkboxData: { display: string; data: ILegalConsent; viewRequired: boolean; viewed: boolean }[] = [];
  @ViewChild('fieldValue')
  public model: NgModel;
  /**
   * Get the value of the component by calling the function
   */
  get value(): boolean {
    return this.innerValue;
  }

  /**
   * Set the value of the component by calling the function
   * @param string val - The string containing selected value.
   */
  set value(v: boolean | undefined) {
    if (v !== this.value) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }
  /** Local variable to hold Slide-in panel Content */
  public panelContent: string;
  /** Local variable to hold Slide-in panel Title */
  public panelTitle: string;
  /** Local variable to hold Slide-in Visibility */
  public showPanel: boolean;
  /** Local variable to hold Can allow to accept Consent */
  public allowToAccept = true;
  /** Local variable to display Consent View Required Message */
  public showViewRequiredMsg = false;
  /** Local variable to hold Data Loaded or not on Slide-In Panel */
  public isDataloaded = false;
  /** Is Download Pdf call initiated */
  private isCallInitiated = false;
  /** Pdf Url */
  public pdfURL = '';
  /** Local variable to hold Is it text Consent */
  public isTextConsent = false;
  /** Local variable to hold Is it Document Consent */
  public isDocumentConsent = false;
  // by the Control Value Accessor
  private readonly onTouchedCallback: () => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly onChangeCallback: (_: any) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onChange: any = () => {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onTouched: any = () => {};

  public statefulService = inject(StatefulService);

  /** Current fieldId reference */
  @Input() public fieldId: string;

  @Input() public consentField: FieldInstance;

  @Input() public isSubmitted: boolean;

  @Input() public required: boolean;

  @Input() public enabled?: boolean;

  /** acknowledgement string with tag or key */
  private checkboxLabelText = '';
  private legalConsent: IEconsentAcknowledgment;
  @Input()
  public set consent(consent: IEconsentAcknowledgment) {
    this.legalConsent = consent;
    if (consent?.title?.length) {
      this.checkboxLabelText = consent.title;
      this.processLinks();
    }
  }

  /**
   * acknowledgement meta data
   */
  private consentLibrary: ILegalConsent[] = [];
  @Input()
  public set consents(value: ILegalConsent[]) {
    this.consentLibrary = value;
    this.processLinks();
  }

  /**
   * Register a function to tell Angular when the value of the input changes
   * @param function fn - Passing function as parameter.
   */
  public registerOnChange(fn): void {
    this.onChange = fn;
  }

  /**
   * Write a value to the input
   */
  public writeValue(value): void {
    this.value = value;
  }

  /**
   * Register a function to call when the input is touched.
   * @param function fn - Passing function as parameter.
   */
  public registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  /**
   * Setting selected selected value from radio list
   * @param string selectedValue - The string containing selected value.
   */
  public setValue(selectedValue: boolean): void {
    if (selectedValue !== this.value) {
      this.value = selectedValue;
      this.onChange(this.value);
      this.onTouched();
    }
  }
  // TEMP: function to add unique Ids to radio buttons based on names.
  public setId(name: string): string {
    return 'id-' + name;
  }

  public onValueChange(value: boolean): void {
    // eslint-disable-next-line no-underscore-dangle
    const _value = value ? value : '';
    if (_value !== this.value) {
      this.onChange(this.value);
      this.onTouched();
    }
  }

  /**
   * Event Emitter for Consent Selection
   */
  @Output() public selectionChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private readonly globalService: GlobalService, public readonly commonService: CommonService) {}

  /**
   * parse Consent Display text by replace with respective $variable$ and hold it in local varible
   * @returns void
   */
  private processLinks(): void {
    if (!this.consentLibrary?.length) {
      return;
    }

    if (!this.checkboxLabelText?.length) {
      return;
    }

    // Split checkbox Label Text with $ to take respective data from consent library and set it in the local variable
    const links = this.checkboxLabelText.split('$');
    this.checkboxData = links.map((link) => {
      const cons = this.consentLibrary?.find((t) => t.key === link);
      return {
        display: cons?.label?.toString() || link,
        data: !!cons ? cons : null,
        viewRequired: !!cons ? Boolean(this.legalConsent?.viewRequired) : false,
        viewed: false
      };
    });
    this.allowToAccept = this.value === true ? true : !this.legalConsent?.viewRequired;
  }

  /**
   * Do some action on Consent Link Click
   * @param item LegalConsent Object
   */
  public async openSlideInPanel(item: ILegalConsent): Promise<void> {
    // Mark Consent is viewed
    this.checkboxData.forEach((data) => {
      if (data?.display?.toString() === item?.key?.toString()) {
        data.viewed = true;
      }
    });

    // Consent is viewed & check allow to accept consent checkbox
    this.setAllowToAccept();

    // If Link Consent then no need to dipslay slide-in panel.
    if (item?.type?.toLowerCase() === 'link') {
      return;
    }

    // If consent type is not link then display it in slid-in panel
    this.isTextConsent = false;
    this.isDocumentConsent = false;
    this.isDataloaded = false;
    this.pdfURL = undefined;
    this.showPanel = true;
    this.panelTitle = item.title;

    // Implement consent link click event bsed on type like text or document or link
    if (item?.type?.toLowerCase() === 'text') {
      this.panelContent = item.content;
      this.isTextConsent = true;
      this.isDocumentConsent = false;
      this.isDataloaded = true;
    } else if (item?.type?.toLowerCase() === 'document') {
      this.isDocumentConsent = true;
      this.isTextConsent = false;
      await this.openDocumentSlideInPanel(item);
    }
  }

  /**
   * Open Cosnet Document Slide-In Panel
   * @param item Legal Consent
   * @returns Void
   */
  public async openDocumentSlideInPanel(item: ILegalConsent): Promise<void> {
    if (this.isCallInitiated) {
      return;
    }
    const disclosureFileName = item.file?.fileName;
    const mimeType = item.file?.mimeType;
    this.panelTitle = item.title;
    this.isDataloaded = false;
    await this.download(disclosureFileName, mimeType);
  }

  /**
   * Download document consent & display it in Slide-In Panel
   * @param value fileName
   * @param mimeType pdf type
   * @returns void
   */
  public download(value: string, mimeType: string): Promise<void> {
    this.isCallInitiated = true;
    this.pdfURL = undefined;
    return firstValueFrom(this.globalService.downloadLegalDocument(value, mimeType))
      .then((blob: Blob) => {
        this.pdfURL = this.globalService.createBlobUrl(blob, mimeType);
        Promise.resolve(true);
        this.isDataloaded = true;
        this.isCallInitiated = false;
      })
      .catch((err) => {
        this.isCallInitiated = false;
        this.commonService.errorHandling(err);
        this.isDataloaded = true;
      });
  }

  /**
   * Print Text type Consent
   */
  public printTextConsent(): void {
    this.globalService.print(this.panelContent, RequestHeaderConst.contentType);
  }

  /**
   * Print Document PDF Content
   */
  public printPDF(): void {
    this.globalService.printPDF(this.pdfURL);
  }

  /**
   * If Consent Link set as 'Required to read' option then we need to display message and not to enable click event
   */
  private setShowViewRequiredMsg(): void {
    if (this.allowToAccept !== true) {
      if (this.value !== true) {
        this.showViewRequiredMsg = true;
        return;
      }
    }
    this.showViewRequiredMsg = false;
  }

  /**
   * Check allow to invoke click event.?
   * If View Required Message is displaying then disable click event
   */
  public get enableClickEvent(): boolean {
    this.setShowViewRequiredMsg();
    return !this.showViewRequiredMsg;
  }

  /**
   * onClick event of Consent Checkbox
   * @param clickEvent Event
   */
  public onClickConsentCheckBox(clickEvent: Event): void {
    clickEvent.stopPropagation();
  }

  public markTouched(): void {
    this.model.control.markAsTouched();
    this.model.control.markAsDirty();
  }

  /**
   * Emit event for tcpa selection
   * @param value boolean
   */
  public onChangeConsentSelection(value): void {
    if (!this.legalConsent.consentRootPath) {
      this.selectionChange.emit(value);
      return; // used for login components out of workflow
    }
    const isAccepted = value;
    const setDefaultBusinessPageIterationId =
      this.legalConsent.consentRootPath.includes('#') && this.consentField.pageIterationId === undefined;
    const pageIterationId = setDefaultBusinessPageIterationId ? 0 : this.consentField.pageIterationId;
    const agreedDatePath = this.legalConsent.consentRootPath + '/agreedDate';
    const isAcceptedPath = this.legalConsent.consentRootPath + '/isAccepted';
    const declinedDatePath = this.legalConsent.consentRootPath + '/declinedDate';
    const acceptanceTextPath = this.legalConsent.consentRootPath + '/acceptanceText';
    const acknowledgeIdPath = this.legalConsent.consentRootPath + '/acknowledgeId';

    if (isAccepted) {
      this.statefulService.wrap(isAcceptedPath, this.consentField.iterationId, pageIterationId).value = value;
      this.statefulService.wrap(agreedDatePath, this.consentField.iterationId, pageIterationId).value = new Date().toISOString();
      this.statefulService.wrap(declinedDatePath, this.consentField.iterationId, pageIterationId).value = null;
      this.statefulService.wrap(acceptanceTextPath, this.consentField.iterationId, pageIterationId).value = this.legalConsent.title;
      this.statefulService.wrap(acknowledgeIdPath, this.consentField.iterationId, pageIterationId).value =
        this.legalConsent.acknowledgeId.toString();
    } else {
      this.statefulService.wrap(isAcceptedPath, this.consentField.iterationId, pageIterationId).value = null;
      this.statefulService.wrap(agreedDatePath, this.consentField.iterationId, pageIterationId).value = null;
      this.statefulService.wrap(declinedDatePath, this.consentField.iterationId, pageIterationId).value = new Date().toISOString();
      this.statefulService.wrap(acceptanceTextPath, this.consentField.iterationId, pageIterationId).value = null;
      this.statefulService.wrap(acknowledgeIdPath, this.consentField.iterationId, pageIterationId).value = null;
    }

    this.selectionChange.emit(value);
  }

  /**
   * Set variable whether able to accept the consent or not based on ViewRequired validation
   */
  public setAllowToAccept(): void {
    const pendingConsent = this.checkboxData.filter((f) => f.viewRequired === true && f.viewed === false);
    this.allowToAccept = !pendingConsent?.length;
  }
}
